import { useEffect, useRef, type MutableRefObject } from 'react';
import { captureLeadPublic, type CaptureLeadPayload } from '@/api/leads';
import type { PendingLeadSnapshot } from './quizProgressStorage';

/**
 * WEB-1716: сколько раз визард пробует дослать не доехавший лид на следующих
 * продвижениях (сверх повторов внутри api/leads.ts).
 */
export const MAX_PENDING_LEAD_RETRIES = 2;

/**
 * WEB-2177 / WEB-1716 (находка 2 шестого прохода свежего взгляда) — КТО
 * ДОБИВАЕТ ВОССТАНОВЛЕННУЮ НЕДООТПРАВКУ.
 *
 * Снимок прогресса научился её ПОМНИТЬ (`pendingLead` в плеере), но добивал её
 * только `handleNext` — то есть следующее продвижение покупателя. Если поле
 * почты стоит на последнем экране, продвигаться после перезагрузки уже
 * некуда (`isCompleted`), `handleNext` не позовётся никогда, и контакт
 * пролежал бы в localStorage сутки и умер вместе с записью: правило было
 * доведено до одного конца из двух.
 *
 * Поэтому повтор пускается и на МОНТИРОВАНИИ — ровно один раз за монтирование
 * (`restoredLeadRetryDone`) и тем же штатным `retryPendingLead`, без второй
 * копии логики отправки и потолка попыток. Ссылка на него живёт в ref
 * (её и отдаёт хук), потому что сама функция объявлена в плеере ниже раннего
 * возврата рендера (заглушка ожидания зонда способов оплаты), а хуки обязаны
 * быть безусловными: эффект без списка зависимостей проверяет ref на каждом
 * кадре и срабатывает на первом, где функция уже есть.
 *
 * `restoredWithoutPending` — в снимке недоотправки не было. Тогда с
 * восстановленной недоотправкой «уже разобрались»: иначе эффект (он без
 * списка зависимостей — см. выше) подхватывал бы и СВЕЖИЙ отказ сервера в
 * этой же сессии и слал повтор, не дожидаясь продвижения. Свежий отказ
 * добивает `handleNext`, как и до этой правки.
 *
 * WEB-3402: вынесено из QuizScreenWizard.tsx без изменений поведения; вызов
 * стоит на прежнем месте порядка хуков плеера.
 */
export function useQuizRestoredLeadRetry(
  isPreview: boolean,
  pendingLeadRef: MutableRefObject<PendingLeadSnapshot | null>,
  restoredWithoutPending: boolean,
): MutableRefObject<(() => void) | null> {
  const retryPendingLeadRef = useRef<(() => void) | null>(null);
  const restoredLeadRetryDone = useRef(restoredWithoutPending);
  useEffect(() => {
    // В предпросмотре автора настоящих лидов не бывает вовсе (см.
    // submitEmailIfPresent в плеере) — добивать нечего и незачем.
    if (isPreview || restoredLeadRetryDone.current) return;
    const pending = pendingLeadRef.current;
    if (!pending || pending.retries >= MAX_PENDING_LEAD_RETRIES) return;
    const retry = retryPendingLeadRef.current;
    if (!retry) return;
    restoredLeadRetryDone.current = true;
    retry();
  });
  return retryPendingLeadRef;
}

/**
 * WEB-1716: лид, который не доехал, больше не теряется молча.
 *
 * Сам api/leads.ts уже повторяет запрос на сетевых сбоях/5xx и сообщает о
 * провале в Sentry. Здесь — второй рубеж: не доехавший лид кладётся в ref и
 * пробуется ещё раз на следующих продвижениях (покупатель к тому моменту
 * ушёл с экрана с полем почты, поэтому адрес надо помнить). Ограничено
 * MAX_PENDING_LEAD_RETRIES, чтобы не долбить сервер, который лежит.
 *
 * Не хук: плеер собирает пару заново на каждом рендере (ниже раннего возврата),
 * как раньше собирал две свои функции. Отдаёт [sendLead, retryPendingLead] —
 * кортеж, а не объект, и параметры позиционные: имена ключей объекта
 * минификатор не сжимает, а запас веса первого экрана покупателя — десятки
 * байт (WEB-3402).
 *
 * WEB-3402: вынесено из QuizScreenWizard.tsx без изменений поведения.
 */
export function createQuizLeadDelivery(
  projectId: string,
  pendingLeadRef: MutableRefObject<PendingLeadSnapshot | null>,
  rememberPendingLead: (next: PendingLeadSnapshot | null) => void,
  setEmailDelivered: (delivered: boolean) => void,
) {
  const sendLead = (payload: CaptureLeadPayload) => {
    Promise.resolve(captureLeadPublic(projectId, payload))
      .then((result) => {
        // Старый контракт (void) и успех — помнить нечего.
        if (result && result.ok === false) {
          rememberPendingLead({ payload, retries: 0 });
        } else {
          rememberPendingLead(null);
          setEmailDelivered(true);
        }
      })
      .catch(() => {
        rememberPendingLead({ payload, retries: 0 });
      });
  };

  const retryPendingLead = () => {
    const pending = pendingLeadRef.current;
    if (!pending || pending.retries >= MAX_PENDING_LEAD_RETRIES) return;
    rememberPendingLead({ ...pending, retries: pending.retries + 1 });
    Promise.resolve(captureLeadPublic(projectId, pending.payload))
      .then((result) => {
        if (!result || result.ok !== false) {
          rememberPendingLead(null);
          setEmailDelivered(true);
        }
      })
      .catch(() => {
        /* остаётся в ref до исчерпания попыток */
      });
  };

  return [sendLead, retryPendingLead] as const;
}
